// client/src/components/secure/HotLeadPanel.js — website engagement on a contact or client:
// the score badge (Cold / Warm / Hot), the plain-English reason, the points behind it and
// the website activity timeline. Read-only; the rules are in lib/hotLeads.js.
import React, { useEffect, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import { Panel, Pill } from './pm/PMKit';

export const TIER_TONE = { hot: 'bad', warm: 'warn', cold: 'slate' };
export const TierBadge = ({ tier, score }) => (tier ? <Pill tone={TIER_TONE[tier]}>{tier}{score != null ? ` · ${score}` : ''}</Pill> : null);

const when = (d) => new Date(d).toLocaleString([], { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
const what = (e) => {
    const where = e.listing_label || e.listing_key;
    switch (e.event_type) {
        case 'page_view': return `Viewed ${where}`;
        case 'repeat_visit': return `Came back to ${where}`;
        case 'favorite': return `Saved ${where} as a favourite`;
        case 'share': return `Shared ${where}`;
        case 'inquiry_started': return `Started an inquiry about ${where}`;
        case 'inquiry_sent': return where ? `Sent an inquiry about ${where}` : 'Sent an inquiry / contact form';
        case 'time_on_page': return `Spent ${e.seconds_bucket >= 300 ? '5+ minutes' : e.seconds_bucket >= 120 ? '2+ minutes' : '30+ seconds'} on ${where}`;
        case 'saved_search': return 'Created a saved search';
        default: return e.label || e.event_type;
    }
};

const HotLeadPanel = ({ contactId, clientId }) => {
    const api = useApiClient();
    const [p, setP] = useState(null);
    const [error, setError] = useState('');
    const [all, setAll] = useState(false);
    useEffect(() => {
        const url = contactId ? `/api/hot-leads/contact/${contactId}` : `/api/hot-leads/client/${clientId}`;
        api.get(url).then(setP).catch((e) => setError(e.message));
    }, [api, contactId, clientId]);
    if (error) return null;           // not migrated / no access: the card simply has no panel
    if (!p) return null;
    const shown = all ? p.timeline : p.timeline.slice(0, 8);
    return (
        <Panel title="Website engagement" actions={p.enabled ? <TierBadge tier={p.tier} score={p.score} /> : <Pill>scoring off</Pill>}
            subtitle={`Behaviour on our website only. Warm at ${p.thresholds.warm}, Hot at ${p.thresholds.hot} points; points halve with age.`}>
            {p.reason && <p className="text-sm text-slate-800 mb-3"><strong>Why:</strong> {p.reason[0].toUpperCase() + p.reason.slice(1)}.</p>}
            {Object.keys(p.by_type || {}).length > 0 && (
                <p className="text-xs text-slate-500 mb-3">
                    {Object.entries(p.by_type).map(([k, v]) => `${k.replace(/_/g, ' ')} ×${v.count} = ${v.points}`).join(' · ')}
                </p>
            )}
            {p.timeline.length === 0 ? (
                <p className="text-sm text-slate-500">No website activity linked to this person yet. Browsing is linked only after they
                    contact us, chat, sign in, or open a listing link their agent sent.</p>
            ) : (
                <ul className="divide-y divide-slate-100">
                    {shown.map((e) => (
                        <li key={e.event_id} className="py-1.5 text-sm flex justify-between gap-3">
                            <span className="text-slate-800">{what(e)}</span>
                            <span className="text-xs text-slate-500 whitespace-nowrap">{when(e.occurred_at)}</span>
                        </li>
                    ))}
                </ul>
            )}
            {p.timeline.length > 8 && (
                <button type="button" className="mt-2 text-sm text-indigo-700 hover:underline" onClick={() => setAll(!all)}>
                    {all ? 'Show less' : `Show all ${p.timeline.length}`}</button>
            )}
            {p.alerts && p.alerts.length > 0 && (
                <p className="text-xs text-slate-500 mt-3">Hot-lead alerts: {p.alerts.map((a) => `${when(a.created_at)}${a.agent_name ? ` → ${a.agent_name}` : ''}`).join('; ')}</p>
            )}
        </Panel>
    );
};

export default HotLeadPanel;
